import { useEffect, useState } from "react";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError } from "@/lib/api";

export const ShareDialog = ({ open, onOpenChange, playlist, onSaved }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setS(null);
    api.get(`/playlists/${playlist.id}/share`).then((r) => setS({ ...r.data, picked: new Set(r.data.user_ids) }))
      .catch((e) => { toast.error(formatApiError(e)); onOpenChange(false); });
  }, [open, playlist.id, onOpenChange]);

  const toggle = (id) => { const n = new Set(s.picked); n.has(id) ? n.delete(id) : n.add(id); setS({ ...s, picked: n }); };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/playlists/${playlist.id}/share`, { everyone: s.everyone, user_ids: [...s.picked] });
      const n = data.everyone ? "everyone" : `${data.user_ids.length} account${data.user_ids.length === 1 ? "" : "s"}`;
      toast.success(data.everyone || data.user_ids.length ? `Shared with ${n}` : "Sharing turned off");
      onSaved?.(data.everyone || data.user_ids.length > 0);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading text-2xl">Share "{playlist.name}"</DialogTitle></DialogHeader>
        <p className="-mt-2 text-xs text-zinc-500">People you share with can play this playlist but can't change, download or favourite it.</p>
        {!s ? <Loader2 className="animate-spin text-zinc-400" /> : (
          <>
            <label className="flex items-center justify-between rounded-md bg-zinc-50 px-4 py-3">
              <span className="flex items-center gap-2 text-sm font-medium"><Users size={15} />Everyone with an account</span>
              <Switch checked={s.everyone} onCheckedChange={(v) => setS({ ...s, everyone: v })} data-testid="share-everyone-switch" />
            </label>
            <div className={`max-h-64 overflow-y-auto rounded-md border border-zinc-200 ${s.everyone ? "pointer-events-none opacity-40" : ""}`} data-testid="share-people-list">
              {s.people.length === 0 && <p className="p-4 text-sm text-zinc-500">No other accounts yet.</p>}
              {s.people.map((u) => (
                <label key={u.id} className="flex cursor-pointer items-center gap-3 border-b border-zinc-100 px-4 py-2.5 last:border-0 hover:bg-zinc-50" data-testid={`share-person-${u.id}`}>
                  <Checkbox checked={s.picked.has(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`share-person-checkbox-${u.id}`} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{u.name || u.email}</span>
                    <span className="block truncate font-mono text-[11px] text-zinc-500">{u.email}</span>
                  </span>
                  <span className="font-mono text-[10px] uppercase text-zinc-400">{u.role}</span>
                </label>
              ))}
            </div>
          </>
        )}
        <DialogFooter>
          <button onClick={save} disabled={!s || busy} data-testid="share-save-button" className="flex items-center gap-2 rounded-full bg-zinc-900 px-6 py-2 text-sm text-white disabled:opacity-40">
            {busy && <Loader2 size={14} className="animate-spin" />}Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
